import React, { useEffect, useState } from "react";
import api from "../utils/axios";
import { useAuth } from "../contexts/AuthContext";
import {
  Card,
  CardContent,
} from "../components/ui/card";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  format,
  parseISO,
  isBefore,
  isWithinInterval,
  addMonths,
} from "date-fns";

const STATUS_COLORS = {
  "In Use": "#4ade80",              // green
  "In Maintenance": "#facc15",      // yellow
  "Available": "#60a5fa",           // blue
  "No Longer Available": "#f87171", // red
};

const STATUS_KEYS = {
  in_use: "In Use",
  in_maintenance: "In Maintenance",
  available: "Available",
  no_longer_available: "No Longer Available",
};

export default function HomeDashboard() {
  const [vehicles, setVehicles] = useState([]);
  const [statusData, setStatusData] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [projectData, setProjectData] = useState([]);
  const [selectedDocType, setSelectedDocType] = useState("All");
  const { isAuthenticated } = useAuth();
  
  const filteredAlerts = alerts.filter((a) => {
    if (selectedDocType === "All") return true;
    return a.type === selectedDocType;
  });

  useEffect(() => {
    // Only make API call if user is authenticated
    if (isAuthenticated) {
      api
        .get("/api/vehicles/")
        .then((res) => {
          setVehicles(res.data);
          generateStatusData(res.data);
          generateAlerts(res.data);
          generateProjectData(res.data);
        })
        .catch((err) => {
          // Handle error silently
        });
    }
  }, [isAuthenticated]);

  const generateStatusData = (data) => {
    const counts = {
      in_use: 0,
      in_maintenance: 0,
      available: 0,
      no_longer_available: 0,
    };

    data.forEach((v) => counts[v.status] += 1);

    const chartData = Object.keys(counts).map((key) => ({
      name: STATUS_KEYS[key],
      value: counts[key],
      color: STATUS_COLORS[STATUS_KEYS[key]],
    }));

    setStatusData(chartData);
  };

const generateProjectData = (data) => {
  const counts = {};

  data.forEach((v) => {
    const name = v.project?.name || "Unassigned";
    counts[name] = (counts[name] || 0) + 1;
  });

  const chartData = Object.keys(counts).map((key, index) => ({
    name: key,
    value: counts[key],
    color: `hsl(${index * 60}, 70%, 50%)`,  // generates different colors
  }));

  setProjectData(chartData);
};


  const generateAlerts = (data) => {
    const allAlerts = [];

    data.forEach((v) => {
      if (v.insurance?.expiry_date) {
        allAlerts.push({
          vehicle: v,
          type: "Insurance",
          expiry_date: v.insurance.expiry_date,
        });
      }
      if (v.roadtax?.expiry_date) {
        allAlerts.push({
          vehicle: v,
          type: "Roadtax",
          expiry_date: v.roadtax.expiry_date,
        });
      }
      if (v.inspection?.expiry_date) {
        allAlerts.push({
          vehicle: v,
          type: "Inspection",
          expiry_date: v.inspection.expiry_date,
        });
      }
      if (v.permits && Array.isArray(v.permits)) {
        v.permits.forEach((p) => {
          if (p.expiry_date) {
            const label =
                p.permit_type.charAt(0).toUpperCase() +
                p.permit_type.slice(1) +
                " Permit";
            allAlerts.push({
              vehicle: v,
              type: label,
              expiry_date: p.expiry_date,
            });
          }
        });
      }
    });

    // Sort by soonest expiry
    allAlerts.sort(
        (a, b) => new Date(a.expiry_date) - new Date(b.expiry_date)
    );

    setAlerts(allAlerts);
  };


  const getDateColor = (dateStr) => {
  const today = new Date();
  const date = parseISO(dateStr);
  const diffInDays = Math.ceil((date - today) / (1000 * 60 * 60 * 24));

  if (isBefore(date, today)) return "bg-red-500 text-white";
  if (diffInDays <= 30) return "bg-yellow-400 text-black";
  if (diffInDays <= 90) return "bg-lime-400 text-black";
  return "bg-green-500 text-white";
};

  const getAlertCounts = () => {
    const today = new Date();
    const counts = { overdue: 0, due30: 0, due90: 0, later: 0 };

    alerts.forEach((a) => {
      const date = parseISO(a.expiry_date);
      const diff = Math.ceil((date - today) / (1000 * 60 * 60 * 24));

      if (isBefore(date, today)) {
        counts.overdue += 1;
      } else if (diff <= 30) {
        counts.due30 += 1;
      } else if (diff <= 90) {
        counts.due90 += 1;
      } else {
        counts.later += 1;
      }
    });

    return counts;
  };


  const alertCounts = getAlertCounts(); 
  return (
    <div className="space-y-6">
      {/* Vehicle Status Pie Chart Section */}
      <div className="flex flex-col md:flex-row gap-4">
  {/* Left: Vehicle Status */}
  <Card className="w-full md:w-1/2 min-h-[400px]">
    <CardContent className="p-4">
      <h2 className="text-lg font-semibold mb-1">
        Vehicle Status Overview ({vehicles.length} Vehicles)
      </h2>
      <div className="flex flex-col md:flex-row items-start justify-between gap-6">
        <div className="space-y-2">
          {statusData.map((entry) => (
            <div key={entry.name} className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded-sm"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-sm">
                {entry.name} ({entry.value})
              </span>
            </div>
          ))}
        </div>
        <div className="w-full flex justify-center">
          <ResponsiveContainer width={300} height={300}>
            <PieChart>
              <Pie
                data={statusData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, value }) => {
                  const RADIAN = Math.PI / 180;
                  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
                  const x = cx + radius * Math.cos(-midAngle * RADIAN);
                  const y = cy + radius * Math.sin(-midAngle * RADIAN);
                  return (
                    <text
                      x={x}
                      y={y}
                      fill="#fff"
                      textAnchor="middle"
                      dominantBaseline="central"
                      fontSize={14}
                      fontWeight="bold"
                    >
                      {value}
                    </text>
                  );
                }}
                labelLine={false}
              >
                {statusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </CardContent>
  </Card>

  {/* Right: Vehicles Deployment Overview */}
  <Card className="w-full md:w-1/2 min-h-[400px]">
    <CardContent className="p-4">
      <h2 className="text-lg font-semibold mb-1">
        Vehicles Deployment Overview ({vehicles.length} Vehicles)
      </h2>
      <div className="flex flex-col md:flex-row items-start justify-between gap-6">
        <div className="space-y-2">
          {projectData.map((entry) => (
            <div key={entry.name} className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded-sm"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-sm">
                {entry.name} ({entry.value})
              </span>
            </div>
          ))}
        </div>
        <div className="w-full flex justify-center">
          <ResponsiveContainer width={300} height={300}>
            <PieChart>
              <Pie
                data={projectData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label
                labelLine
              >
                {projectData.map((entry, index) => (
                  <Cell key={`cell-proj-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </CardContent>
  </Card>
</div>


      {/* Service Date Alert Table */}
<Card>
  <CardContent className="p-4">
    <div className="flex items-center gap-2 mt-4">
      <label className="text-sm font-medium">Filter by Document:</label>
      <select
          value={selectedDocType}
          onChange={(e) => setSelectedDocType(e.target.value)}
          className="border rounded px-2 py-1 text-sm"
      >
        <option value="All">All</option>
        <option value="Insurance">Insurance</option>
        <option value="Roadtax">Roadtax</option>
        <option value="Inspection">Inspection</option>
        <option value="Brunei Permit">Brunei Permit</option>
        <option value="Sabah Permit">Sabah Permit</option>
        <option value="Sarawak Permit">Sarawak Permit</option>
      </select>
    </div>
    <div className="flex flex-wrap justify-between items-center mb-4">
      <h2 className="text-lg font-semibold">
        Upcoming Service Dates ({alerts.length})
      </h2>
      {/* Color Legend */}

      <div className="flex gap-3 text-sm items-center flex-wrap">
  <div className="flex items-center gap-1">
    <div className="w-4 h-4 rounded-sm bg-red-500" />
    <span>Overdue ({alertCounts.overdue})</span>
  </div>
  <div className="flex items-center gap-1">
    <div className="w-4 h-4 rounded-sm bg-yellow-400" />
    <span>1–30 days ({alertCounts.due30})</span>
  </div>
  <div className="flex items-center gap-1">
    <div className="w-4 h-4 rounded-sm bg-lime-400" />
    <span>31–90 days ({alertCounts.due90})</span>
  </div>
  <div className="flex items-center gap-1">
    <div className="w-4 h-4 rounded-sm bg-green-500" />
    <span>>90 days ({alertCounts.later})</span>
  </div>
</div>
    </div>

    {alerts.length === 0 ? (
      <p className="text-sm text-muted-foreground">No upcoming service due.</p>
    ) : (
      <div className="max-h-[340px] overflow-y-auto border rounded">
        <div className="grid grid-cols-[40px_1fr_1fr_1fr_1fr_1fr_1fr] font-semibold bg-gray-100 p-3 border-b">
          <div>#</div>
          <div>Plate</div>
          <div>Type</div>
          <div>Status</div>
          <div>Document</div>
          <div>Expiry Date</div>
          <div>Deployment</div>
        </div>

        {filteredAlerts.map((a, index) => (
            <div
                key={`${a.vehicle.id}-${a.type}`}
                className={`grid grid-cols-[40px_1fr_1fr_1fr_1fr_1fr_1fr] items-center px-3 py-2 border-b ${
                    index % 2 === 0 ? "bg-white" : "bg-gray-50"
                }`}
            >
              <div>{index + 1}</div>
              <div>{a.vehicle.plate_number}</div>
              <div>{a.vehicle.car_type.replaceAll("_", " ")
                  .split(" ")
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(" ")}</div>
              <div>
                {a.vehicle.status
                    .replaceAll("_", " ")
                    .split(" ")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ")}
              </div>
              <div>{a.type}</div>
              <div>
      <span
          className={`px-2 py-1 rounded font-medium text-sm ${getDateColor(
              a.expiry_date
          )}`}
      >
        {format(parseISO(a.expiry_date), "yyyy-MM-dd")}
      </span>
              </div>
              <div>{a.vehicle.project?.name || "-"}</div>
            </div>
        ))}

      </div>
    )}
  </CardContent>
</Card>

    </div>
  );
}
